/**
 * Theme state: a palette (hue) and a mode (light/dark), stored per browser.
 *
 * Both are attributes on <html> rather than React state on a provider, so the very first paint is
 * already correct — see the inline script in index.html. A provider alone would render one frame
 * with the default theme and flash.
 */

export const THEMES = [
  { id: 'classic', name: 'Classic', note: 'Near-black with a mint accent — the original.' },
  { id: 'ocean', name: 'Ocean', note: 'Deep blue-teal, cool and high contrast.' },
  { id: 'forest', name: 'Forest', note: 'Deep green, softer than classic.' },
  { id: 'sky', name: 'Sky', note: 'Slate blue, the lightest of the dark set.' },
  { id: 'nature', name: 'Nature', note: 'Earthy browns and olive. The warmest.' },
  { id: 'sporty', name: 'Sporty', note: 'Neutral charcoal with a hot coral accent.' },
  { id: 'midnight', name: 'Midnight', note: 'Pure greys, no hue at all.' },
  { id: 'plum', name: 'Plum', note: 'Deep violet with magenta.' },
] as const

export type ThemeId = (typeof THEMES)[number]['id']
export type ModeId = 'dark' | 'light' | 'system'

const THEME_KEY = 'smsgw.theme'
const MODE_KEY = 'smsgw.mode'

const DEFAULT_THEME: ThemeId = 'classic'
const DEFAULT_MODE: ModeId = 'dark'

const isTheme = (v: unknown): v is ThemeId => THEMES.some((t) => t.id === v)
const isMode = (v: unknown): v is ModeId => v === 'dark' || v === 'light' || v === 'system'

export function getTheme(): ThemeId {
  try {
    const v = localStorage.getItem(THEME_KEY)
    return isTheme(v) ? v : DEFAULT_THEME
  } catch {
    // Private browsing and some embedded webviews throw on localStorage access rather than
    // returning null. A theme is not worth breaking the app over.
    return DEFAULT_THEME
  }
}

export function getMode(): ModeId {
  try {
    const v = localStorage.getItem(MODE_KEY)
    return isMode(v) ? v : DEFAULT_MODE
  } catch {
    return DEFAULT_MODE
  }
}

/** 'system' resolves against the OS preference; everything else is itself. */
export function resolveMode(mode: ModeId): 'dark' | 'light' {
  if (mode !== 'system') return mode
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark'
}

export function applyTheme(theme: ThemeId, mode: ModeId) {
  const root = document.documentElement
  const resolved = resolveMode(mode)
  root.setAttribute('data-theme', theme)
  root.setAttribute('data-mode', resolved)

  // Keeps native UI — form controls, scrollbars, the URL bar on mobile — in step with the app.
  root.style.colorScheme = resolved

  // Drives the phone's status bar / address bar colour. Read from the live computed token so it
  // can never drift from the stylesheet.
  const bg = getComputedStyle(root).getPropertyValue('--bg').trim()
  if (bg) {
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'theme-color'
      document.head.appendChild(meta)
    }
    meta.content = `rgb(${bg})`
  }
}

export function setTheme(theme: ThemeId) {
  try {
    localStorage.setItem(THEME_KEY, theme)
  } catch {
    /* not fatal — the choice just will not survive a reload */
  }
  applyTheme(theme, getMode())
}

export function setMode(mode: ModeId) {
  try {
    localStorage.setItem(MODE_KEY, mode)
  } catch {
    /* as above */
  }
  applyTheme(getTheme(), mode)
}

/**
 * Keeps `system` honest: without this, choosing System and then changing the OS setting leaves the
 * app on whatever it resolved to at load.
 */
export function watchSystemMode(): () => void {
  if (typeof matchMedia !== 'function') return () => {}
  const mq = matchMedia('(prefers-color-scheme: light)')
  const handler = () => {
    if (getMode() === 'system') applyTheme(getTheme(), 'system')
  }
  mq.addEventListener('change', handler)
  return () => mq.removeEventListener('change', handler)
}
